import React, { useCallback, useState, useEffect } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import './autocomplete.module.css';
import { Doctor } from 'types/doctor';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import DoctorItem from './DoctorItem';

let timer: NodeJS.Timeout;

interface AutocompleteFromProps {
  onSelect(value: Doctor | null): void;
  disabled?: boolean;
  label: string;
  placeholder: string;
  selected: Doctor | null;
  helperText?: string;
  error?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

const DoctorsAutoComplete: React.FC<AutocompleteFromProps> = ({
  onSelect,
  label,
  placeholder,
  selected,
  helperText = '',
  error = false,
  disabled,
  ref,
}) => {
  const [loading, setLoading] = useState(false);
  const [filtered, setFiltered] = useState<Doctor[]>([]);
  const [options, setOptions] = useState<Doctor[]>([]);

  const handleSelect = useCallback(
    (option: Doctor) => {
      onSelect(option);
      setFiltered([]);
    },
    [onSelect],
  );

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  const findDoctors = useCallback(async (term: string) => {
    setLoading(true);
    api
      .get('/doctors', { params: { rows: 200, page: 1, name: term, active: 1 } })
      .then(response => {
        setOptions(response.data.items);
      })
      .catch(err => {
        toast.error('Ocorreu um erro ao buscar os médicos');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  function handleChange(value: string) {
    clearTimeout(timer);

    if (value.length < 2) {
      return;
    }

    timer = setTimeout(() => findDoctors(value), 400);
  }

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      handleHomeEndKeys
      openOnFocus
      disabled={disabled}
      value={selected}
      options={filtered}
      noOptionsText="Nenhum médico encontrado"
      getOptionLabel={option => option.name}
      onChange={(event, newValue) => onSelect(newValue)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <DoctorItem handleSelect={handleSelect} option={option} selected={selected} />
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          inputRef={ref}
          disabled={disabled}
          onBlur={() => setFiltered([])}
          onChange={e => handleChange(e.target.value)}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText || 'Digite 2 ou mais caracteres'}
          fullWidth
        />
      )}
    />
  );
};

export default DoctorsAutoComplete;
